<!-- src/views/appointments/AppointmentForm.vue -->
<template>
  <div class="appointment-form">
    <!-- Back button and page title -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <router-link :to="returnPath" class="btn btn-outline-primary">
        <i class="bi bi-arrow-left me-2"></i>Volver
      </router-link>
      <h1>{{ isEditMode ? 'Editar Cita' : 'Nueva Cita' }}</h1>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Form content -->
    <div v-else class="row">
      <div class="col-md-8 mx-auto">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="submitForm">
              <!-- Alert for errors -->
              <div v-if="error" class="alert alert-danger mb-4">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                {{ error }}
              </div>

              <!-- Success message for Google Calendar sync -->
              <div v-if="calendarSyncSuccess" class="alert alert-success mb-4">
                <i class="bi bi-check-circle-fill me-2"></i>
                {{ calendarSyncSuccess }}
              </div>

              <div class="row g-3">
                <!-- Patient selection -->
                <div class="col-12 mb-3">
                  <label for="patient" class="form-label">Paciente *</label>
                  <div v-if="preSelectedPatientId" class="input-group">
                    <span class="form-control">{{ getPatientName(preSelectedPatientId) }}</span>
                    <button class="btn btn-outline-secondary" type="button" @click="clearPatient">
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                  <select v-else id="patient" class="form-select" v-model="form.patient" required>
                    <option value="" disabled>Seleccionar paciente</option>
                    <option v-for="patient in patients" :key="patient.id" :value="patient.id">
                      {{ patient.first_name }} {{ patient.last_name }}
                    </option>
                  </select>
                  <div v-if="validationErrors.patient" class="text-danger mt-1 small">
                    {{ validationErrors.patient }}
                  </div>
                </div>

                <!-- Date and Time -->
                <div class="col-md-6">
                  <label for="fecha" class="form-label">Fecha *</label>
                  <!-- Prevent timezone issues by handling the input value directly -->
                  <input type="date" class="form-control" id="fecha" :value="form.fecha"
                    @input="form.fecha = $event.target.value" required>
                  <div v-if="validationErrors.fecha" class="text-danger mt-1 small">
                    {{ validationErrors.fecha }}
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="hora" class="form-label">Hora *</label>
                  <input type="time" class="form-control" id="hora" v-model="form.hora" required>
                  <div v-if="validationErrors.hora" class="text-danger mt-1 small">
                    {{ validationErrors.hora }}
                  </div>
                </div>



                <!-- Treatment Type -->
                <div class="col-md-6">
                  <label for="tipo_tratamiento" class="form-label">Tipo de Tratamiento *</label>
                  <select id="tipo_tratamiento" class="form-select" v-model="form.tipo_tratamiento" required>
                    <option value="" disabled>Seleccionar tipo de tratamiento</option>
                    <option value="Valoración Inicial">Valoración Inicial</option>
                    <option value="Presupuesto">Presupuesto</option>
                    <option value="Limpieza">Limpieza</option>
                    <option value="Colocación de Aparatología">Colocación de Aparatología</option>
                    <option value="Control">Control</option>
                    <option value="Retiro de Aparatología">Retiro de Aparatología</option>
                    <option value="Control Retención">Control Retención</option>
                    <option value="Emergencia">Emergencia</option>
                    <option value="Otro">Otro</option>
                  </select>
                  <div v-if="validationErrors.tipo_tratamiento" class="text-danger mt-1 small">
                    {{ validationErrors.tipo_tratamiento }}
                  </div>
                </div>

                                <!-- Specialty -->
                                <div class="col-md-6">
                  <label for="specialty" class="form-label">Especialidad</label>
                  <select id="specialty" class="form-select" v-model="form.specialty">
                    <option value="" disabled>Seleccionar especialidad</option>
                    <option value="ortodoncia">Ortodoncia</option>
                    <option value="cirugia">Cirugía</option>
                    <option value="endodoncia">Endodoncia</option>
                    <option value="implantologia">Implantología</option>
                    <option value="odontopediatria">Odontopediatría</option>
                    <option value="periodoncia">Periodoncia</option>
                    <option value="rehabilitacion">Rehabilitación</option>
                  </select>
                  <div v-if="validationErrors.specialty" class="text-danger mt-1 small">
                    {{ validationErrors.specialty }}
                  </div>
                </div>
                
                <!-- Status -->
                <div class="col-12" v-if="isEditMode">
                  <label for="estado" class="form-label">Estado</label>
                  <select id="estado" class="form-select" v-model="form.estado">
                    <option value="programada">Programada</option>
                    <option value="completada">Completada</option>
                    <option value="inasistencia">Inasistencia</option>
                    <option value="confirmada">Confirmada</option>
                  </select>
                </div>

                <!-- Duration field -->
                <div class="col-md-6">
                  <label for="duracion" class="form-label">Duración (min) *</label>
                  <select id="duracion" class="form-select" v-model="form.duracion" required>
                    <option value="5">5 minutos</option>
                    <option value="15">15 minutos</option>
                    <option value="30">30 minutos</option>
                    <option value="45">45 minutos</option>
                    <option value="60">1 hora</option>
                    <option value="75">1 hora 15 minutos</option>
                    <option value="90">1 hora 30 minutos</option>
                    <option value="105">1 hora 45 minutos</option>
                    <option value="120">2 horas</option>
                  </select>
                </div>

                <!-- Location field -->
                <div class="col-12">
                  <label for="location" class="form-label">Ubicación</label>
                  <input type="text" class="form-control" id="location" v-model="form.location"
                    placeholder="Ej: Ágora Medical Center">
                </div>

                <!-- Notes -->
                <div class="col-12">
                  <label for="notes" class="form-label">Notas</label>
                  <textarea id="notes" class="form-control" v-model="form.notes" rows="3"
                    placeholder="Información adicional sobre la cita..."></textarea>
                </div>

                <!-- Google Calendar Sync Option -->
                <div class="col-12">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="syncWithGoogleCalendar"
                      id="syncWithGoogleCalendar">
                    <label class="form-check-label" for="syncWithGoogleCalendar">
                      Sincronizar con Google Calendar
                    </label>
                  </div>
                </div>

                <!-- Form actions -->
                <div class="col-12 mt-4">
                  <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-outline-secondary me-2" @click="resetForm">
                      Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary" :disabled="submitting">
                      <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ isEditMode ? 'Guardar cambios' : 'Guardar cita' }}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <!-- Google Calendar Sync Section (Edit mode only) -->
            <div v-if="isEditMode && !googleCalendarSynced" class="mt-4 pt-4 border-top">
              <h5 class="mb-3">Sincronización con Google Calendar</h5>
              <p class="text-muted mb-3">
                Puedes sincronizar esta cita con tu Google Calendar para recibir recordatorios y gestionar mejor tu
                agenda.
              </p>
              <button type="button" class="btn btn-outline-primary" @click="syncWithGoogle" :disabled="syncingCalendar">
                <i class="bi bi-google me-2"></i>
                <span v-if="syncingCalendar" class="spinner-border spinner-border-sm me-2" role="status"
                  aria-hidden="true"></span>
                Sincronizar con Google Calendar
              </button>
            </div>

            <!-- Already synced message -->
            <div v-if="isEditMode && googleCalendarSynced" class="mt-4 pt-4 border-top">
              <div class="d-flex align-items-center">
                <i class="bi bi-check-circle-fill text-success me-2" style="font-size: 1.2rem;"></i>
                <h5 class="mb-0">Cita sincronizada con Google Calendar</h5>
              </div>
              <div class="mt-3">
                <button type="button" class="btn btn-sm btn-outline-primary me-2" @click="syncWithGoogle"
                  :disabled="syncingCalendar">
                  <i class="bi bi-arrow-repeat me-1"></i>
                  <span v-if="syncingCalendar" class="spinner-border spinner-border-sm me-1" role="status"
                    aria-hidden="true"></span>
                  Actualizar sincronización
                </button>
                <button type="button" class="btn btn-sm btn-outline-danger" @click="deleteFromGoogleCalendar"
                  :disabled="deletingFromCalendar">
                  <i class="bi bi-trash me-1"></i>
                  <span v-if="deletingFromCalendar" class="spinner-border spinner-border-sm me-1" role="status"
                    aria-hidden="true"></span>
                  Eliminar de Google Calendar
                </button>
              </div>
            </div>

            <!-- Google Calendar Authorization Notice -->
            <div v-if="showGoogleAuthNeeded" class="alert alert-info mt-4">
              <i class="bi bi-info-circle-fill me-2"></i>
              Necesitas autorizar el acceso a Google Calendar para usar esta función.
              <button class="btn btn-sm btn-primary ms-2" @click="initiateGoogleAuth">
                Autorizar ahora
              </button>
            </div>

            <!-- Delete appointment button (Edit mode only) -->
            <div v-if="isEditMode" class="mt-4 pt-4 border-top">
              <h5 class="text-danger mb-3">Zona de peligro</h5>
              <p class="text-muted mb-3">
                Al eliminar la cita, se borrará permanentemente del sistema.
                Esta acción no se puede deshacer.
              </p>
              <button type="button" class="btn btn-outline-danger" @click="showDeleteConfirmation = true">
                <i class="bi bi-trash me-2"></i>Eliminar cita
              </button>

              <!-- Delete confirmation modal -->
              <div class="modal fade" :class="{ show: showDeleteConfirmation, 'd-block': showDeleteConfirmation }"
                tabindex="-1" role="dialog" aria-hidden="true">
                <div class="modal-dialog">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title">Confirmar eliminación</h5>
                      <button type="button" class="btn-close" @click="showDeleteConfirmation = false"
                        aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                      <p>¿Estás seguro que deseas eliminar esta cita?</p>
                      <p class="text-danger">Esta acción no se puede deshacer.</p>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary"
                        @click="showDeleteConfirmation = false">Cancelar</button>
                      <button type="button" class="btn btn-danger" @click="deleteAppointment" :disabled="deleting">
                        <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status"
                          aria-hidden="true"></span>
                        Eliminar cita
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Modal backdrop -->
              <div v-if="showDeleteConfirmation" class="modal-backdrop fade show"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { appointmentService, patientService, googleCalendarService } from '@/services/api';
import { formatDateForDisplay, formatDateForAPI, formatDateForInput, getTodayForInput } from '@/utils/dateUtils';

export default {
  name: 'AppointmentForm',
  data() {
    return {
      form: {
        patient: '',
        fecha: '',
        hora: '',
        duracion: 30,
        tipo_tratamiento: '',
        specialty: 'ortodoncia', // Default to ortodoncia
        estado: 'programada',
        notes: '',
        location: 'Ágora Medical Center'
      },
      originalForm: {},
      patients: [],
      preSelectedPatientId: null,
      validationErrors: {},
      loading: true,
      submitting: false,
      deleting: false,
      error: null,
      showDeleteConfirmation: false,
      syncWithGoogleCalendar: true, // Default to sync with Google Calendar
      googleCalendarSynced: false,
      syncingCalendar: false,
      deletingFromCalendar: false,
      showGoogleAuthNeeded: false,
      calendarSyncSuccess: null
    };
  },
  computed: {
    isEditMode() {
      return this.$route.path.includes('/edit');
    },
    appointmentId() {
      return this.$route.params.id;
    },
    returnPath() {
      if (this.isEditMode) {
        return `/appointments/${this.appointmentId}`;
      }

      // If there's a pre-selected patient, return to patient detail
      if (this.preSelectedPatientId) {
        return `/patients/${this.preSelectedPatientId}`;
      }

      // Otherwise, return to appointments list
      return '/appointments';
    }
  },
  async created() {
    try {
      // Load all patients
      const patientsResponse = await patientService.getAll();
      this.patients = patientsResponse.data;

      // Check if a patient ID was provided in the query params
      const patientId = this.$route.query.patient;
      if (patientId) {
        this.preSelectedPatientId = parseInt(patientId);
        this.form.patient = this.preSelectedPatientId;
      }

      if (this.isEditMode) {
        await this.fetchAppointment();
        await this.checkGoogleCalendarSync();
      } else {
        // Set default date and time for new appointments
        this.form.fecha = getTodayForInput();

        // Round up to the nearest half hour
        const now = new Date();
        const minutes = now.getMinutes();
        const roundedMinutes = minutes < 30 ? 30 : 0;
        const hours = minutes < 30 ? now.getHours() : now.getHours() + 1;

        this.form.hora = `${hours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'No se pudieron cargar los datos necesarios. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }

    // Check Google authorization status
    try {
      await this.checkGoogleAuthStatus();
    } catch (error) {
      console.error('Error checking Google auth status:', error);
    }

  },
  methods: {
    async checkGoogleAuthStatus() {
      try {
        const response = await googleCalendarService.checkAuthStatus();
        if (!response.data.is_authorized) {
          // Pre-emptively show the authorization button
          this.showGoogleAuthNeeded = true;
        }
      } catch (error) {
        // If we get any error, show the auth button
        this.showGoogleAuthNeeded = true;
      }
    },
    
    async fetchAppointment() {
      try {
        const response = await appointmentService.get(this.appointmentId);

        // Use utility function for consistent date handling
        this.form = {
          ...response.data,
          fecha: formatDateForInput(response.data.fecha)
        };
        this.originalForm = { ...this.form };

        console.log('Appointment loaded with date:', this.form.fecha);
      } catch (error) {
        console.error('Error fetching appointment:', error);
        this.error = 'No se pudo cargar la información de la cita.';
      }
    },

    // Check if appointment is already synced with Google Calendar
    async checkGoogleCalendarSync() {
      try {
        const response = await googleCalendarService.checkSync(this.appointmentId);
        if (response && response.data && response.data.is_synced) {
          this.googleCalendarSynced = true;
        }
      } catch (error) {
        console.error('Error checking Google Calendar sync:', error);
        // If we get a 401 Unauthorized error, we need to authorize
        if (error.response && error.response.status === 401) {
          this.showGoogleAuthNeeded = true;
        }
      }
    },

    // Initiate Google Calendar OAuth flow
    async initiateGoogleAuth() {
      try {
        const response = await googleCalendarService.getAuthUrl();
        // Redirect to the Google authorization URL
        window.location.href = response.data.authorization_url;
      } catch (error) {
        console.error('Error initiating Google auth:', error);
        this.error = 'No se pudo iniciar la autorización con Google Calendar.';
      }
    },

    // Sync appointment with Google Calendar
    async syncWithGoogle() {
      this.syncingCalendar = true;
      this.error = null;
      this.calendarSyncSuccess = null;

      try {
        const response = await googleCalendarService.syncAppointment(this.appointmentId);
        this.googleCalendarSynced = true;
        this.calendarSyncSuccess = 'La cita se ha sincronizado correctamente con Google Calendar.';
      } catch (error) {
        console.error('Error syncing with Google Calendar:', error);
        // If we get a 401 Unauthorized error, we need to authorize
        if (error.response && error.response.status === 401) {
          this.showGoogleAuthNeeded = true;
        } else {
          this.error = 'No se pudo sincronizar con Google Calendar.';
        }
      } finally {
        this.syncingCalendar = false;
      }
    },

    // Delete appointment from Google Calendar
    async deleteFromGoogleCalendar() {
      this.deletingFromCalendar = true;
      this.error = null;
      this.calendarSyncSuccess = null;

      try {
        await googleCalendarService.deleteFromCalendar(this.appointmentId);
        this.googleCalendarSynced = false;
        this.calendarSyncSuccess = 'La cita se ha eliminado correctamente de Google Calendar.';
      } catch (error) {
        console.error('Error deleting from Google Calendar:', error);
        this.error = 'No se pudo eliminar la cita de Google Calendar.';
      } finally {
        this.deletingFromCalendar = false;
      }
    },

    validateForm() {
      this.validationErrors = {};
      let isValid = true;

      // Patient validation
      if (!this.form.patient) {
        this.validationErrors.patient = 'Debes seleccionar un paciente';
        isValid = false;
      }

      // Date validation
      if (!this.form.fecha) {
        this.validationErrors.fecha = 'La fecha es obligatoria';
        isValid = false;
      }

      // Time validation
      if (!this.form.hora) {
        this.validationErrors.hora = 'La hora es obligatoria';
        isValid = false;
      }

      // Treatment type validation
      if (!this.form.tipo_tratamiento) {
        this.validationErrors.tipo_tratamiento = 'El tipo de tratamiento es obligatorio';
        isValid = false;
      }

      return isValid;
    },

    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.submitting = true;
      this.error = null;

      try {
        // Use utility function instead of manual day subtraction
        const formData = {
          ...this.form,
          fecha: formatDateForAPI(this.form.fecha)
        };

        let appointmentId;

        if (this.isEditMode) {
          await appointmentService.update(this.appointmentId, formData);
          appointmentId = this.appointmentId;
        } else {
          const response = await appointmentService.create(formData);
          appointmentId = response.data.id;
        }

        // Handle Google Calendar sync
        if ((this.isEditMode && (this.googleCalendarSynced || this.syncWithGoogleCalendar)) ||
          (!this.isEditMode && this.syncWithGoogleCalendar)) {
          try {
            await googleCalendarService.syncAppointment(appointmentId);
          } catch (error) {
            console.error('Error syncing with Google Calendar:', error);
          }
        }

        this.$router.push(`/appointments/${appointmentId}`);
      } catch (error) {
        console.error('Error saving appointment:', error);
        this.error = 'Error al guardar la cita. Por favor, intenta de nuevo.';
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      if (this.isEditMode) {
        // In edit mode, reset to original values
        this.form = { ...this.originalForm };
      } else {
        // In create mode, clear the form but keep the pre-selected patient
        const patientId = this.preSelectedPatientId;

        this.form = {
          patient: patientId || '',
          fecha: getTodayForInput(),
          hora: this.getDefaultTime(),
          duracion: 30,
          tipo_tratamiento: '',
          specialty: 'ortodoncia', // Keep default specialty
          estado: 'programada',
          notes: '',
          location: 'Ágora Medical Center'
        };
      }

      this.validationErrors = {};
      this.error = null;
    },

    getDefaultTime() {
      // Round up to the nearest half hour
      const now = new Date();
      const minutes = now.getMinutes();
      const roundedMinutes = minutes < 30 ? 30 : 0;
      const hours = minutes < 30 ? now.getHours() : now.getHours() + 1;
      
      return `${hours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
    },

    async deleteAppointment() {
      this.deleting = true;
      try {
        // If appointment is synced with Google Calendar, delete from there first
        if (this.googleCalendarSynced) {
          try {
            await googleCalendarService.deleteFromCalendar(this.appointmentId);
          } catch (error) {
            console.error('Error deleting from Google Calendar:', error);
            // Continue with deletion even if Google Calendar deletion fails
          }
        }

        await appointmentService.delete(this.appointmentId);
        this.showDeleteConfirmation = false;
        // Redirect to appointments list
        this.$router.push('/appointments');
      } catch (error) {
        console.error('Error deleting appointment:', error);
        this.error = 'No se pudo eliminar la cita. Por favor, intenta de nuevo.';
        this.showDeleteConfirmation = false;
      } finally {
        this.deleting = false;
      }
    },

    clearPatient() {
      this.preSelectedPatientId = null;
      this.form.patient = '';
    },

    getPatientName(patientId) {
      const patient = this.patients.find(p => p.id === parseInt(patientId));
      return patient ? `${patient.first_name} ${patient.last_name}` : 'Paciente desconocido';
    }
  }
};
</script>